import {
  WorkflowTaskId,
  WorkflowTask,
  ActionDefinition,
} from "common/flowmaster/types/task_config";
import {
  Workflow,
  ContextId,
  WorkflowInputId,
  WorkflowInput,
  WorkflowInstanceId,
  Prompt,
  WorkflowContext,
  WorkflowInstanceDetail,
} from "common/flowmaster/types/workflow";
import {OrganisationId} from "common/types/organisation";
import {Topic} from "common/types/topic";
import React from "react";
import Inputs from "./inputs";
import Tasks from "./tasks";
import Context from "./context";
import Prerequistes from "./prerequisites";
import Fieldset from "./styled/fieldset";

interface WorkflowEditorProps {
  organisationId: OrganisationId;
  workflow: Workflow;

  addWorkflowInput: (name) => void;
  updateWorkflowContext: (workflowContextId: ContextId) => void;
  updateWorkflowInput: (
    workflowInputId: WorkflowInputId,
    data: WorkflowInput,
  ) => void;
  removeWorkflowInput: (workflowInputId: WorkflowInputId) => void;
  reorderWorkflowInputs: (
    workflowInputId: WorkflowInputId,
    order: number,
  ) => void;

  addWorkflowTask: (name) => void;
  updateWorkflowTask: (
    workflowTaskId: WorkflowTaskId,
    data: WorkflowTask,
  ) => void;
  removeWorkflowTask: (workflowInputId: WorkflowInputId) => void;
  reorderWorkflowTasks: (
    id: WorkflowTaskId,
    movedItemId: WorkflowTaskId,
  ) => void;
  addPrerequisite: () => void;
  updatePrerequisiteInputValue: (
    id: WorkflowInstanceId,
    inputId: WorkflowInputId,
    value: string,
  ) => void;
  removePrerequisite: (id: WorkflowInstanceId) => void;
  updateWorkflowInstanceName: (id: WorkflowInstanceId, name: string) => void;
  actionDefinitions: ActionDefinition[];
  workflows: Workflow[];
  prompts: Prompt[];
  contexts: WorkflowContext[];
  embeddingTopics: Topic[];
  existingPrerequisites: WorkflowInstanceDetail[];
}

export const WorkflowEditor: React.FC<WorkflowEditorProps> = ({
  organisationId,
  workflow,
  updateWorkflowContext,
  addWorkflowInput,
  updateWorkflowInput,
  removeWorkflowInput,
  reorderWorkflowInputs,
  addWorkflowTask,
  updateWorkflowTask,
  removeWorkflowTask,
  reorderWorkflowTasks,
  addPrerequisite,
  updatePrerequisiteInputValue,
  removePrerequisite,
  updateWorkflowInstanceName,
  actionDefinitions,
  workflows,
  existingPrerequisites,
  prompts,
  contexts,
  embeddingTopics,
}) => {
  const disabled = workflow.is_locked;
  const params = new URLSearchParams(location.search);
  const taskFromUrl = params.get("task");

  const selectedContext = contexts.find(
    ctx => ctx.id === workflow.context_id,
  ) as WorkflowContext;

  return (
    <div>
      {disabled && (
        <Fieldset
          style={{
            display: "flex",
            justifyContent: "center",
            backgroundColor: "#ffc5c5",
            color: "red",
            padding: "20px",
            fontWeight: "bold",
            textAlign: "center",
            fontSize: "1.1em",
          }}
        >
          <div>
            {workflow.is_data_asset
              ? "Data Asset is locked."
              : "Workflow is locked. Unlock to Edit."}
          </div>
        </Fieldset>
      )}
      <Context
        selectedContext={workflow.context_id}
        items={contexts}
        onUpdate={updateWorkflowContext}
        disabled={disabled}
      />
      {selectedContext.prerequisite_workflow_contexts.length > 0 && (
        <Prerequistes
          organisationId={organisationId}
          existingPrerequisites={existingPrerequisites}
          prerequisites={workflow.prerequisites}
          workflows={workflows.filter(({context_id}) =>
            selectedContext.prerequisite_workflow_contexts.includes(context_id),
          )}
          addPrerequisite={addPrerequisite}
          removePrerequisite={removePrerequisite}
          updateWorkflowInstanceName={updateWorkflowInstanceName}
          updatePrerequisiteInputValue={updatePrerequisiteInputValue}
          disabled={workflow.is_locked}
        />
      )}
      <Inputs
        items={workflow.inputs.values}
        onAdd={addWorkflowInput}
        onUpdate={updateWorkflowInput}
        onDelete={removeWorkflowInput}
        onReorder={reorderWorkflowInputs}
        disabled={workflow.is_locked}
      />
      <Tasks
        items={workflow.tasks.values}
        onAdd={addWorkflowTask}
        onUpdateItem={updateWorkflowTask}
        onDeleteItem={removeWorkflowTask}
        onReorder={reorderWorkflowTasks}
        workflows={workflows}
        actionDefinitions={actionDefinitions}
        context={selectedContext}
        inputs={workflow.inputs}
        prompts={prompts}
        organisationId={organisationId}
        embeddingTopics={embeddingTopics}
        taskFromUrl={taskFromUrl}
        disabled={workflow.is_locked}
      />
    </div>
  );
};
